/**
 * Source-faithful records for the thirteen built-in v1 courses.
 *
 * This is deliberately a detached data boundary.  The records are copied
 * from `shared/sim.js`'s `TRACK_DEFS`; importing this module does not register
 * a course, build a spline, or change v2's six-course runtime catalog.  A
 * later adapter can choose how to scale these points and how to represent v1
 * checkpoint/bridge behaviour without silently changing either source.
 *
 * `shared/sim.js` does not put a `themeKey` on its built-in definitions.  The
 * legacy renderers resolve `TRACK_THEMES[track.key]` when that field is
 * absent, so `theme` below records that resolved presentation fallback.  Its
 * palette/info/floor-pattern values come from the legacy presentation tables,
 * not from an assertion that the v2 authored courses are equivalent routes.
 */

export type LegacyTrackPoint = readonly [number, number];
export type LegacyTunnel = readonly [number, number];

export interface LegacyThemePalette {
  readonly road: string;
  readonly kerbDef: string;
  readonly edge: string;
  readonly venue: string;
  readonly accent: string;
  readonly fog: string;
}

export type LegacyFloorPattern = "planks" | "tile" | "checker";

export interface LegacyTrackThemeMetadata {
  /** Resolved legacy `TRACK_THEMES` key (the track id fallback). */
  readonly themeKey: string;
  /** Resolved legacy floor painter key when no independent override exists. */
  readonly floorKey: string;
  /** Resolved legacy props key when no independent override exists. */
  readonly propsKey: string;
  readonly palette: LegacyThemePalette;
  readonly floorPattern: LegacyFloorPattern;
  readonly info: {
    readonly accent: string;
    readonly tag: string;
  };
}

export interface LegacyTrackDefinition {
  /** The original `TRACK_DEFS` object key. */
  readonly id: string;
  readonly name: string;
  /** Unsmoothed, unscaled v1 control points, in the original pixel units. */
  readonly raw: readonly LegacyTrackPoint[];
  /** Road half-width in original v1 units (edge is centreline +/- width). */
  readonly width: number;
  /** Centerline fractions used by v1's jump marker conversion. */
  readonly jumps: readonly number[];
  /** Centerline fraction pairs used by v1's tunnel marker conversion. */
  readonly tunnels: readonly LegacyTunnel[];
  readonly theme: LegacyTrackThemeMetadata;
}

const THEME_METADATA = {
  bartop: {
    themeKey: "bartop", floorKey: "bartop", propsKey: "bartop",
    palette: { road: "#4a4038", kerbDef: "#b23a2e", edge: "#efe0bb", venue: "#17110d", accent: "#d6a951", fog: "#100d0b" },
    floorPattern: "planks",
    info: { accent: "#f3c95c", tag: "The original after-hours sprint" },
  },
  brewery: {
    themeKey: "brewery", floorKey: "brewery", propsKey: "brewery",
    palette: { road: "#485e67", kerbDef: "#e2a31d", edge: "#d7b35d", venue: "#15191b", accent: "#de854c", fog: "#0c1114" },
    floorPattern: "tile",
    info: { accent: "#73d5ea", tag: "Steel, steam and one big crossover" },
  },
  garden: {
    themeKey: "garden", floorKey: "garden", propsKey: "garden",
    palette: { road: "#9a8665", kerbDef: "#f1ead1", edge: "#e6e0bc", venue: "#101a13", accent: "#8fd36d", fog: "#0b130d" },
    floorPattern: "checker",
    info: { accent: "#a7d66f", tag: "Fast grass-side curves" },
  },
  rooftop: {
    themeKey: "rooftop", floorKey: "rooftop", propsKey: "rooftop",
    palette: { road: "#47435e", kerbDef: "#16d9d0", edge: "#f0dcff", venue: "#090b18", accent: "#dc6eff", fog: "#080913" },
    floorPattern: "tile",
    info: { accent: "#ff62ca", tag: "Neon switchbacks above the city" },
  },
  cellar: {
    themeKey: "cellar", floorKey: "cellar", propsKey: "cellar",
    palette: { road: "#554a40", kerbDef: "#672d20", edge: "#d8c3a2", venue: "#100d0c", accent: "#b87151", fog: "#090706" },
    floorPattern: "planks",
    info: { accent: "#f2a65a", tag: "An ossuary hook beneath the last-orders vault" },
  },
  oktoberfest: {
    themeKey: "oktoberfest", floorKey: "oktoberfest", propsKey: "oktoberfest",
    palette: { road: "#9b6b3e", kerbDef: "#1763a3", edge: "#fff0c2", venue: "#18130c", accent: "#e9b947", fog: "#110d08" },
    floorPattern: "planks",
    info: { accent: "#4da5e4", tag: "The three-crossing festival special" },
  },
  icebar: {
    themeKey: "icebar", floorKey: "icebar", propsKey: "icebar",
    palette: { road: "#3f7187", kerbDef: "#176da0", edge: "#e7fbff", venue: "#0a141c", accent: "#77dff2", fog: "#071018" },
    floorPattern: "tile",
    info: { accent: "#a9f4ff", tag: "Glassy arcs and double jumps" },
  },
  distillery: {
    themeKey: "distillery", floorKey: "distillery", propsKey: "distillery",
    palette: { road: "#554941", kerbDef: "#a74f27", edge: "#f0d0ae", venue: "#17100c", accent: "#d7834f", fog: "#100a07" },
    floorPattern: "planks",
    info: { accent: "#d77d4f", tag: "Copper coils and alternating bends" },
  },
  roadhouse: {
    themeKey: "roadhouse", floorKey: "roadhouse", propsKey: "roadhouse",
    palette: { road: "#393238", kerbDef: "#f0d9b5", edge: "#f2d29b", venue: "#18100b", accent: "#e99353", fog: "#110b08" },
    floorPattern: "planks",
    info: { accent: "#ffb35c", tag: "Dust, neon and one last fuel stop" },
  },
  harbour: {
    themeKey: "harbour", floorKey: "harbour", propsKey: "harbour",
    palette: { road: "#344956", kerbDef: "#e1b957", edge: "#d5eef2", venue: "#07141a", accent: "#5bc6dd", fog: "#071014" },
    floorPattern: "tile",
    info: { accent: "#65b9d2", tag: "Stormwater, lock gates and the dry dock" },
  },
  velvet: {
    themeKey: "velvet", floorKey: "velvet", propsKey: "velvet",
    palette: { road: "#261f2a", kerbDef: "#c69a58", edge: "#f3d8c7", venue: "#160b13", accent: "#d56d9b", fog: "#10080d" },
    floorPattern: "checker",
    info: { accent: "#e7b6cf", tag: "Art Deco dog-legs after midnight" },
  },
  courtyard: {
    themeKey: "courtyard", floorKey: "courtyard", propsKey: "courtyard",
    palette: { road: "#dfc497", kerbDef: "#1269aa", edge: "#fff0cc", venue: "#17150f", accent: "#e5c15c", fog: "#100f0b" },
    floorPattern: "planks",
    info: { accent: "#ffd36b", tag: "Sun tiles, fountains and a pergola launch" },
  },
  moon: {
    themeKey: "moon", floorKey: "moon", propsKey: "moon",
    palette: { road: "#565d70", kerbDef: "#7b6bb3", edge: "#dce2ff", venue: "#080914", accent: "#99a8ff", fog: "#070810" },
    floorPattern: "tile",
    info: { accent: "#a9c7ff", tag: "Low-gravity lines beyond last orders" },
  },
} as const satisfies Record<string, LegacyTrackThemeMetadata>;

function deepFreeze<T>(value: T): T {
  if (value !== null && typeof value === "object" && !Object.isFrozen(value)) {
    Object.freeze(value);
    for (const child of Object.values(value as Record<string, unknown>)) deepFreeze(child);
  }
  return value;
}

const definitions = deepFreeze({
  // Track 1: "Bar Top" — flowing loop with a chicane, tunnel on the top
  // straight, jump ramp on the bottom straight.
  bartop: {
    id: "bartop", name: "Bar Top",
    raw: [
      [200, 200], [750, 150], [1300, 200], [1400, 300], [1400, 600], [1300, 720],
      [1050, 760], [950, 650], [820, 640], [720, 760], [400, 780], [180, 700], [120, 450], [140, 300],
    ],
    width: 78, jumps: [0.66], tunnels: [[0.04, 0.14]], theme: THEME_METADATA.bartop,
  },
  // Track 2: "Brewery Floor" — deliberate figure-8 with a bridge crossover.
  brewery: {
    id: "brewery", name: "Brewery Floor",
    raw: [
      [560, 320], [300, 140], [120, 320], [260, 560], [460, 650], [640, 540],
      [900, 380], [1040, 300], [1260, 160], [1420, 380], [1300, 620], [1060, 690], [840, 600], [680, 460],
    ],
    width: 92, jumps: [0.56], tunnels: [], theme: THEME_METADATA.brewery,
  },
  garden: {
    id: "garden", name: "Hop Garden",
    raw: [
      [1425, 470], [1458, 556], [1406, 637], [1284, 691], [1134, 711], [997, 708], [892, 705], [805, 719],
      [709, 753], [581, 790], [427, 804], [282, 777], [188, 711], [168, 623], [213, 537], [285, 470],
      [343, 419], [365, 368], [362, 303], [371, 220], [427, 136], [539, 77], [686, 63], [828, 97],
      [933, 162], [997, 232], [1045, 287], [1110, 323], [1209, 354], [1328, 400],
    ],
    width: 82, jumps: [0.07], tunnels: [], theme: THEME_METADATA.garden,
  },
  rooftop: {
    id: "rooftop", name: "Neon Rooftop",
    raw: [
      [1280, 470], [1357, 537], [1343, 605], [1236, 646], [1102, 656], [1008, 666], [959, 708], [908, 780],
      [817, 837], [703, 837], [612, 780], [561, 708], [512, 666], [418, 656], [284, 646], [177, 605],
      [163, 537], [240, 470], [335, 423], [373, 381], [351, 319], [333, 238], [381, 171], [495, 153],
      [623, 183], [721, 219], [799, 219], [897, 183], [1025, 153], [1139, 171], [1187, 238], [1169, 319],
      [1147, 381], [1185, 423],
    ],
    width: 76, jumps: [], tunnels: [], theme: THEME_METADATA.rooftop,
  },
  cellar: {
    id: "cellar", name: "Keg Catacombs",
    raw: [
      [760, 850], [520, 820], [300, 740], [150, 580], [140, 380], [260, 230], [480, 170], [650, 260],
      [740, 390], [850, 320], [930, 160], [1150, 130], [1340, 260], [1430, 430], [1380, 600], [1220, 700],
      [1050, 670], [900, 560], [760, 620], [764, 690], [824, 791],
    ],
    width: 82, jumps: [], tunnels: [[0.42, 0.50]], theme: THEME_METADATA.cellar,
  },
  oktoberfest: {
    id: "oktoberfest", name: "Pretzel Platz",
    raw: [
      [760, 352], [936, 364], [1094, 400], [1218, 454], [1296, 519], [1321, 587], [1293, 647], [1216, 691],
      [1102, 712], [965, 706], [822, 671], [689, 610], [582, 529], [513, 435], [488, 339], [507, 250],
      [567, 178], [655, 132], [760, 116], [865, 132], [953, 178], [1013, 250], [1032, 339], [1007, 435],
      [938, 529], [831, 610], [698, 671], [555, 706], [418, 712], [304, 691], [227, 647], [199, 587],
      [224, 519], [302, 454], [426, 400], [584, 364],
    ],
    width: 84, jumps: [0.22, 0.72], tunnels: [], theme: THEME_METADATA.oktoberfest,
  },
  icebar: {
    id: "icebar", name: "Vodka Ice Bar",
    raw: [
      [1420, 470], [1398, 545], [1332, 615], [1227, 675], [1090, 721], [931, 750], [760, 760], [589, 750],
      [430, 721], [293, 675], [188, 615], [122, 545], [100, 470], [122, 395], [188, 325], [293, 265],
      [430, 219], [589, 190], [760, 180], [931, 190], [1090, 219], [1227, 265], [1332, 325], [1398, 395],
    ],
    width: 88, jumps: [0.1, 0.6], tunnels: [], theme: THEME_METADATA.icebar,
  },
  distillery: {
    id: "distillery", name: "Copper Coil Works",
    raw: [
      [1384, 470], [1402, 547], [1336, 613], [1217, 653], [1097, 672], [1008, 692], [942, 734], [867, 794],
      [760, 844], [632, 855], [521, 816], [454, 744], [423, 672], [390, 619], [320, 579], [221, 534],
      [136, 470], [118, 393], [184, 327], [303, 287], [423, 268], [512, 248], [578, 206], [653, 146],
      [760, 96], [888, 85], [999, 124], [1066, 196], [1097, 268], [1130, 321], [1200, 361], [1299, 406],
    ],
    width: 80, jumps: [0.52], tunnels: [[0.2, 0.28]], theme: THEME_METADATA.distillery,
  },
  roadhouse: {
    id: "roadhouse", name: "Last Chance Lager",
    raw: [
      [150, 180], [500, 100], [1000, 120], [1350, 200], [1450, 360], [1360, 520], [1100, 600], [850, 530],
      [650, 420], [430, 480], [330, 680], [180, 800], [70, 650], [100, 420],
    ],
    width: 88, jumps: [0.09], tunnels: [], theme: THEME_METADATA.roadhouse,
  },
  harbour: {
    id: "harbour", name: "Blackwater Quay",
    raw: [
      [354, 155], [650, 90], [1050, 120], [1350, 240], [1420, 420], [1280, 510], [1060, 470], [910, 390],
      [820, 540], [1040, 690], [1260, 700], [1380, 840], [1120, 920], [760, 850], [620, 700], [450, 810],
      [160, 760], [60, 520], [180, 350], [285, 270],
    ],
    width: 84, jumps: [], tunnels: [[0.05, 0.11]], theme: THEME_METADATA.harbour,
  },
  velvet: {
    id: "velvet", name: "Velvet Draught",
    raw: [
      [300, 780], [170, 660], [140, 470], [200, 290], [370, 180], [570, 290], [720, 150], [890, 150],
      [1030, 290], [1230, 180], [1390, 290], [1440, 470], [1360, 660], [1240, 780], [1050, 670], [890, 810],
      [710, 810], [550, 670],
    ],
    width: 80, jumps: [], tunnels: [[0.44, 0.52]], theme: THEME_METADATA.velvet,
  },
  courtyard: {
    id: "courtyard", name: "Cerveza Sol Courtyard",
    raw: [
      [550, 840], [350, 760], [260, 620], [320, 470], [160, 350], [120, 180], [300, 70], [600, 100],
      [760, 220], [920, 100], [1220, 70], [1400, 180], [1360, 350], [1200, 470], [1260, 620], [1170, 760],
      [970, 840],
    ],
    width: 84, jumps: [0.40], tunnels: [[0.23, 0.29]], theme: THEME_METADATA.courtyard,
  },
  moon: {
    id: "moon", name: "Moon Tap One",
    raw: [
      [760, 156], [898, 254], [1052, 280], [1093, 375], [1133, 460], [1093, 545], [1052, 640], [898, 666],
      [760, 765], [622, 666], [468, 640], [427, 545], [388, 460], [427, 375], [468, 280], [622, 254],
    ],
    width: 86, jumps: [0.08], tunnels: [[0.55, 0.61]], theme: THEME_METADATA.moon,
  },
} as const satisfies Record<string, LegacyTrackDefinition>);

/** Immutable lookup by the original v1 `TRACK_DEFS` key. */
export const LEGACY_TRACK_DEFINITIONS = definitions;

/** Original declaration order, retained for deterministic rotation/imports. */
export const LEGACY_TRACK_CATALOG: readonly LegacyTrackDefinition[] = Object.freeze(Object.values(definitions));

/** Stable id union for callers that need to constrain a legacy route choice. */
export type LegacyTrackId = keyof typeof LEGACY_TRACK_DEFINITIONS;

export const LEGACY_TRACK_IDS: readonly LegacyTrackId[] = Object.freeze(
  Object.keys(LEGACY_TRACK_DEFINITIONS) as LegacyTrackId[],
);

/** Camel-case alias for adapters that use the v2 catalog naming convention. */
export const legacyTrackCatalog = LEGACY_TRACK_CATALOG;

export function getLegacyTrackDefinition(id: string): LegacyTrackDefinition | undefined {
  return Object.prototype.hasOwnProperty.call(LEGACY_TRACK_DEFINITIONS, id)
    ? LEGACY_TRACK_DEFINITIONS[id as LegacyTrackId]
    : undefined;
}
